// API entity types for the Ops Board (Step 5c). These mirror the read-only list
// endpoints (/api/aircraft, /api/assets, /api/staff, /api/students), the existing
// reference-data endpoints, and GET /api/bookings.

export interface Base {
  id: string;
  name: string;
  short_code: string;
  ui_color: string;
  icao: string | null;
  timezone: string;
  operating_hours_start: string | null;
  operating_hours_end: string | null;
  is_active: boolean;
}

export interface Aircraft {
  registration: string;
  model: string | null;
  aircraft_category: string;
  base_icao: string | null;
  current_base_icao: string | null;
  availability_mode: 'school_owned' | 'shared_charter' | 'third_party';
  approval_required: boolean;
  approval_contact: string | null;
  // Capability & serviceability (Step 5c.2).
  capability_normal: string | null;
  capability_current: string | null; // set only when degraded (≠ normal)
  serviceable: boolean;
  status_note: string | null;
  expected_return_date: string | null; // ISO date; null + unserviceable ⇒ "UFN"
  unserviceable_from: string | null; // ISO date — maintenance commencement; band suppressed before this
  is_active: boolean;
}

export interface Asset {
  id: string;
  name: string;
  short_code: string | null;
  category: string | null;
  ui_color: string | null;
  base_id: string;
  capacity: number | null;
  ownership: 'school_owned' | 'shared' | 'external';
  availability_mode: 'always' | 'restricted' | 'conditional';
  approval_required: boolean;
  require_booking_confirmation: boolean;
  operating_hours: unknown;
  timezone: string | null;
  ppr_required: boolean;
  is_active: boolean;
}

export interface Staff {
  id: string;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  preferred_name: string | null;
  pilot_code: string | null;
  // PR-000059 — admin-assigned ops-board chip colour. NULL means DERIVE one from the id
  // (lib/opsBoard/chipChannels.ts), not "no colour".
  ui_color: string | null;
  role: string | null;
  home_base_id: string | null;
  current_base_id: string | null;
  availability_mode: 'always' | 'restricted' | 'conditional';
  approval_required: boolean;
}

// The bookable-person list (Phase 4): students + clients. `kind` defaults the participant
// capacity in the picker; structured name fields render the company label (personDisplayLabel).
export interface Student {
  id: string;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  preferred_name: string | null;
  pilot_code: string | null;
  training_stage: string | null;
  home_base_id: string | null;
  kind: 'student' | 'client';
}

export interface BookingKind {
  id: string;
  name: string;
  ui_color: string;
  is_maintenance: boolean;
  is_unavailability: boolean; // Feature B — generic resource blocker (slate, muted on the board)
  is_active: boolean;
}

// Coded reason lists (Feature C) — booking + cancellation pickers. Online-only on the board
// (not cached in IndexedDB — see OpsBoard; offline-fresh loads have an empty picker by design).
export interface ReasonCode {
  id: string;
  context: 'booking' | 'cancellation';
  label: string;
  is_active: boolean;
  sort_order: number;
}

export interface TrainingActivity {
  id: string;
  name: string;
  category: string | null;
  default_activity_mins: number | null;
  is_active: boolean;
}

// Row-type visibility filters for the board (Step 5c.2) — all on by default.
export interface RowFilters {
  aircraft: boolean;
  instructors: boolean;
  assets: boolean;
}

export type ApprovalStatus =
  | 'not_required'
  | 'pending_staff'
  | 'pending_asset'
  | 'pending_both'
  | 'approved'
  | 'declined';

export type BookingStatus = 'enquiry' | 'quoted' | 'pending' | 'confirmed' | 'cancelled';

export type ExternalAircraftSource = 'crosshire' | 'client_supplied';

// Distinct display label for an external (non-fleet) aircraft allocation:
//   'Crosshire · VH-ABC (C172)' / 'Client a/c · VH-ABC (C172)'.
export function externalAircraftLabel(a: {
  external_aircraft_source: ExternalAircraftSource | null;
  external_aircraft_type: string | null;
  external_aircraft_registration: string | null;
}): string {
  if (!a.external_aircraft_source) return '';
  const prefix = a.external_aircraft_source === 'crosshire' ? 'Crosshire' : 'Client a/c';
  const reg = a.external_aircraft_registration ?? '—';
  const type = a.external_aircraft_type ? ` (${a.external_aircraft_type})` : '';
  return `${prefix} · ${reg}${type}`;
}

export interface Allocation {
  id: string;
  aircraft_registration: string | null;
  asset_id: string | null;
  // External aircraft (Phase 1) — NOT in the fleet. Present iff external_aircraft_source is set.
  external_aircraft_source: ExternalAircraftSource | null;
  external_aircraft_type: string | null;
  external_aircraft_registration: string | null;
  external_aircraft_invoicer: string | null;
  approval_status: 'not_required' | 'pending' | 'approved' | 'declined';
  booking_confirmed_via: string | null;
  booking_confirmation_note: string | null;
  booking_confirmed_at: string | null;
}

export interface BookingTrainee {
  id: string;
  full_name: string;
  preferred_name: string | null;
}

// Participant roles (Step 5c.3) — validated string list. safety_pilot/observer removed S32
// (CASR 141/142 has only student/instructor; reinstatement arrives as capacities, not roles).
export type ParticipantRole = 'student' | 'instructor';

// Per-participant CAPACITY (S30) — the capacity in which the person acts on this booking.
// external_* are people NOT in the system (no user_id; identified by external_name).
export type ParticipantCapacity = 'instructor' | 'student' | 'client' | 'external_instructor' | 'external_examiner';

export interface ParticipantUser {
  id: string;
  full_name: string;
  preferred_name: string | null;
  pilot_code: string | null;
  // Structured name (optional — old cached booking shapes predate these fields; the
  // backend participant-user select now returns them for the CODE - First Last label).
  first_name?: string | null;
  last_name?: string | null;
}

export interface Participant {
  id: string;
  user_id: string | null;        // null for EXTERNAL participants
  role: ParticipantRole;
  capacity: ParticipantCapacity; // S30
  external_name: string | null;  // set for external_* capacities
  rank: number | null;
  notified: boolean;
  // No usable email address, for a student or client only (false for every other capacity).
  // Derived server-side in serializeBooking — the ADDRESS ITSELF NEVER SHIPS, because this payload
  // is cached offline on every device. OPTIONAL: provisional rows built offline (lib/offline/
  // overlay.ts) and booking shapes cached before this release carry neither, and an absent value
  // is NOT a warning (see lib/notifyWarning.ts).
  email_missing?: boolean;
  user: ParticipantUser | null;
}

// Company settings exposed to the board (Step 5c.3) — non-sensitive subset.
export interface CompanySettings {
  participant_hierarchy_mode: 'equal' | 'ranked';
  waitlist_allocation_policy: 'ops_admin' | 'any_instructor';
  default_buffer_pre_mins: number;
  default_buffer_post_mins: number;
  // The board's three, and ONLY these three (COMPANY-SETTINGS-ADMIN §3): the standard booking
  // length for the form's duration seed, and the two chip colour channels. Everything else on
  // the model is read through /api/company-settings/admin by the admin screen — this payload
  // is offline-cached on every device and stays minimal.
  default_booking_mins: number;
  chip_top_bar_channel: 'booking_kind' | 'instructor' | 'base';
  chip_fill_channel: 'booking_kind' | 'instructor' | 'base';
}

// §B collision payload (error.details) returned on a hard-block 409.
export interface BookingCollision {
  booking_id: string;
  booking_status: BookingStatus;
  // PR-000027 (clash popup): the clashing booking's own title so the popup lists WHAT it clashed
  // with. Participant names stay redacted server-side — a title is the operator's own free text.
  title: string | null;
  // PR-000046: the clashing booking is itself a maintenance block (server-supplied).
  is_maintenance?: boolean;
  block_start_at: string | null;
  block_end: string | null;
  line: { kind: 'aircraft' | 'asset' | 'person'; label: string };
}
export interface CollisionDetails {
  // 'external_overlap' (PR-000023) is a SOFT challenge on the same normalised external rego —
  // amber "Book anyway" (resubmit with ack_external_overlap), no waitlist option.
  // 'maintenance_displacement' (PR-000046): an edited MAINTENANCE booking collides with confirmed
  // booking(s) — confirming resubmits with displace_confirmed and bumps them to the waitlist.
  reason: 'confirmed_overlap' | 'pending_displacement' | 'external_overlap' | 'maintenance_displacement';
  requiresAck?: 'save_as_waitlist' | 'displace_pending' | 'ack_external_overlap' | 'displace_confirmed';
  canWaitlist?: boolean;
  collisions: BookingCollision[];
}

export interface Booking {
  id: string;
  base_id: string;
  pilot_id: string | null;
  trainee_id: string | null;
  trainee: BookingTrainee | null;
  activity_id: string | null;
  booking_kind_id: string | null;
  title: string | null;              // Feature D — free-text booking title (drives the chip label)
  cancel_reason_id: string | null;   // Feature C — coded cancellation reason
  activity_note: string | null;
  // STAFF-ONLY note. Returned by the booking read routes, which are staff-gated (students and
  // clients 403 on both). Never render this on a student/client surface.
  // Booking reminder to the assigned instructor. AMENDED 2026-08-25: reminder_enabled is the
  // "Always remind the instructor" OVERRIDE, not an arm flag — a reminder is sent automatically
  // when the booking has an internal_note or an outstanding approval. reminder_sent_at is the
  // server's fired-once stamp (read-only here).
  // All three are OPTIONAL for the same reason created_by is (see below): rows cached offline
  // before this release predate them, so a cached row legitimately carries none of them.
  internal_note?: string | null;
  reminder_enabled?: boolean;
  reminder_sent_at?: string | null;
  block_start_at: string | null;
  dep_time_planned: string | null;
  arr_time_planned: string | null;
  dep_timezone: string;
  long_brief_mins: number | null;
  short_brief_mins: number | null;
  activity_mins: number | null;
  buf_pre_mins: number;
  buf_post_mins: number;
  booking_status: BookingStatus;
  approval_status: ApprovalStatus;
  student_notified: boolean;
  outside_hours: boolean;
  modified_after_notify: boolean;
  // 0.12.7 Part A — who last recorded telling the client about the modification, and when. Set ⇒
  // the panel's pen becomes a tick; every modification nulls both again. OPTIONAL for the same
  // reason as the reminder fields above: rows cached offline before this release carry neither.
  modify_notified_at?: string | null;
  modify_notified_by?: string | null;
  cancel_reason: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
  // Maintenance lifecycle (Step 5c.2.1)
  maintenance_returned_at: string | null;
  maintenance_return_confirmed_by: string | null;
  maintenance_late_return_reason: string | null; // free-text reason if returned after the planned ECD
  // Multi-participant + capacity + waitlist (Step 5c.3)
  participants: Participant[];
  max_participants: number | null;
  participants_limit_hard: boolean;
  waitlisted: boolean;
  waitlisted_at: string | null;
  waitlist_allocated_by: string | null;
  waitlist_allocated_at: string | null;
  // Recurring bookings (Phase 2). Set on a generated series occurrence. series_overridden flips
  // true once an occurrence is individually edited (Phase 3 edit-scope skips it).
  series_id: string | null;
  series_occurrence_date: string | null;
  series_overridden: boolean;
  // Phase 4. clamped: monthly/yearly date didn't exist → moved to month-end. assignee_conflict: a
  // pre-filled default assignee collides (§B) — flagged, not blocked.
  series_clamped: boolean;
  series_assignee_conflict: boolean;
  updated_at: string;
  // PR-000031 — creator provenance (present in the API's full-row serialization; optional here
  // because older cached rows predate the board reading it). Drives the own-booking drag gate:
  // workflow tier may drag any booking, everyone else only bookings they created (mirrors the
  // backend canEditAll rule in updateBooking).
  created_by?: string | null;
  asset_allocations: Allocation[];
  // Offline sync (Step 5d Phase 2). sync_state: offline-CREATION lifecycle only
  // (null/absent = ordinary online booking); 'conflicted' renders the conflict badge.
  sync_state?: 'confirmed' | 'conflicted' | null;
  origin_sync_op_id?: string | null;
  // FRONTEND-ONLY overlay marker (never sent to or by the API, never cached): this row
  // is synthesized from / shadowed by a queued offline op awaiting drain.
  _pending_op?: { queueKey: string; kind: 'create' | 'edit' | 'cancel' };
}

// ── Copy a booking (recurring/copy Phase 1) ─────────────────────────────────────
// One external (non-fleet) aircraft row in the booking form's allocation list. Mirrors the
// form's internal ExtAcRow; lives here so the pure copy-draft helper (buildCopyDraft) can
// produce it without importing the React form module.
export interface BookingFormExternalAircraft {
  source: ExternalAircraftSource;
  type: string;
  registration: string;
  invoicer: string;
}

// A create-mode seed for <BookingForm>. Carries ONLY the fields the create path
// (bookingCreateSchema) AND the form actually model — see buildCopyDraft. Crew and the
// date/time are deliberately NOT seeded (the user re-picks them). `notCopied` is the FIXED
// list of setup the create path can't reproduce (client/PO/invoice/buffers/DG/pax/task-type/
// brief-mins), so the form can show a "not copied" notice rather than silently dropping them.
export interface BookingFormSeed {
  base_id: string;
  activity_id: string;          // '' = none
  booking_kind_id: string;      // '' = none
  title: string;                // '' = none
  activity_mins: number | null; // drives the duration field; date/time are left for the user
  notes: string;                // '' = none
  internal_note: string;        // '' = none — staff-only note, carried by a copy like notes
  max_participants: string;     // '' = uncapped (the form holds this as a string)
  participants_limit_hard: boolean;
  aircraftRegs: string[];
  assetIds: string[];
  externalAc: BookingFormExternalAircraft[];
  notCopied: string[];
}

// ── Person picker search (Phase 5) ──────────────────────────────────────────────
// Fields a <PersonCombobox> option can be matched on. Students/clients carry the structured
// fields; staff (no structured fields selected) fall back to full_name tokens. `code` is the
// staff pilot_code (e.g. RAMMM) so the enquiries assignee picker matches by code too.
export interface PersonSearchFields {
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  full_name?: string;
  code?: string | null;
}

// Prefix match the query against pilot code OR first name OR last name OR company (case-insensitive),
// with a full_name-token fallback (covers staff). Returns a RANK (lower = better: code → first-name →
// surname → company → token) or null when nothing matches. Empty query matches everything (ranked
// last) so the picker shows the full list. Being OR-across-fields is what makes it inclusive — e.g.
// "Be" surfaces a person whose SURNAME starts with "Be" even if their first name doesn't match, and
// "RAMMM" surfaces the staff member whose code starts with it.
export function personMatchRank(p: PersonSearchFields, query: string): number | null {
  const q = query.trim().toLowerCase();
  if (!q) return 5;
  const code = (p.code ?? '').toLowerCase();
  const f = (p.first_name ?? '').toLowerCase();
  const l = (p.last_name ?? '').toLowerCase();
  const c = (p.company ?? '').toLowerCase();
  if (code.startsWith(q)) return 0;
  if (f.startsWith(q)) return 1;
  if (l.startsWith(q)) return 2;
  if (c.startsWith(q)) return 3;
  if ((p.full_name ?? '').toLowerCase().split(/\s+/).some((t) => t.startsWith(q))) return 4;
  return null;
}
